import { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import { API_ENDPOINT } from '../services/common';

export default function FirmwareVersionPicker({ model, onVersionSelect }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showBeta, setShowBeta] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState('');

  useEffect(() => {
    fetch(`${API_ENDPOINT}/firmware/versions?model=${model}`, { credentials: 'include' })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load firmware versions');
        return res.json();
      })
      .then((data) => {
        setVersions(data.versions || []);
        // Pre-select latest stable
        const latestStable = (data.versions || []).find((v) => !v.prerelease);
        if (latestStable) {
          setSelectedVersion(latestStable.version);
          onVersionSelect(latestStable.version);
        }
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [model, onVersionSelect]);

  const handleVersionChange = (event) => {
    const version = event.target.value;
    setSelectedVersion(version);
    onVersionSelect(version);
  };

  const filteredVersions = showBeta ? versions : versions.filter((v) => !v.prerelease);

  if (loading) return <CircularProgress size={24} />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box sx={{ mb: 2 }}>
      <FormControl fullWidth size="small" sx={{ mb: 1 }}>
        <InputLabel>Firmware Version</InputLabel>
        <Select
          value={selectedVersion}
          label="Firmware Version"
          onChange={handleVersionChange}
        >
          {filteredVersions.map((v) => (
            <MenuItem key={v.version} value={v.version}>
              v{v.version}
              {v.prerelease && (
                <Chip label="BETA" size="small" color="warning" sx={{ ml: 1 }} />
              )}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControlLabel
        control={<Switch checked={showBeta} onChange={(e) => setShowBeta(e.target.checked)} size="small" />}
        label="Show beta versions"
      />
      {selectedVersion && versions.find((v) => v.version === selectedVersion)?.prerelease && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          This is a pre-release build and may be unstable.
        </Alert>
      )}
    </Box>
  );
}
